import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/lib/api";
import { centsToInput, formatCents, maskReaisInput, parseReaisInput } from "@/lib/format";
import type { BeneficioCatalogo, ColunaFlash } from "@/data/beneficios";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";

interface Formulario {
  nome: string;
  colunaFlash: string;
  pj: boolean;
  clt: boolean;
  fixoPj: string;
  fixoClt: string;
}

const VAZIO: Formulario = { nome: "", colunaFlash: "", pj: true, clt: false, fixoPj: "", fixoClt: "" };

const doBeneficio = (b: BeneficioCatalogo): Formulario => ({
  nome: b.nome,
  colunaFlash: b.colunaFlash,
  pj: b.pj,
  clt: b.clt,
  fixoPj: b.fixoPj > 0 ? centsToInput(b.fixoPj) : "",
  fixoClt: b.fixoClt > 0 ? centsToInput(b.fixoClt) : "",
});

/**
 * Benefícios da empresa: nome livre, a coluna da planilha da Flash em que saem,
 * para quais vínculos valem e o valor fixo de cada vínculo.
 */
export const BeneficiosFixosCard = () => {
  const [catalogo, setCatalogo] = useState<BeneficioCatalogo[]>([]);
  const [colunas, setColunas] = useState<ColunaFlash[]>([]);
  const [carregando, setCarregando] = useState(true);
  /** null = fechado; "novo" = criando; senão o benefício em edição. */
  const [editando, setEditando] = useState<BeneficioCatalogo | "novo" | null>(null);
  const [form, setForm] = useState<Formulario>(VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState<BeneficioCatalogo | null>(null);

  const load = useCallback(async () => {
    setCarregando(true);
    try {
      const [lista, cols] = await Promise.all([
        api.get<BeneficioCatalogo[]>("/beneficios/catalogo"),
        api.get<ColunaFlash[]>("/beneficios/colunas-flash"),
      ]);
      setCatalogo(lista);
      setColunas(cols);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao carregar benefícios");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const abrir = (b: BeneficioCatalogo | "novo") => {
    setForm(b === "novo" ? VAZIO : doBeneficio(b));
    setEditando(b);
  };

  const sistema = editando !== null && editando !== "novo" && !!editando.sistema;

  const salvar = async () => {
    if (form.nome.trim().length < 2) {
      toast.error("Informe o nome do benefício");
      return;
    }
    if (!form.colunaFlash) {
      toast.error("Escolha a coluna da planilha da Flash");
      return;
    }
    if (!form.pj && !form.clt) {
      toast.error("Marque PJ, CLT ou os dois");
      return;
    }
    let fixoPj = 0;
    let fixoClt = 0;
    try {
      fixoPj = form.pj && form.fixoPj.trim() ? parseReaisInput(form.fixoPj) : 0;
      fixoClt = form.clt && form.fixoClt.trim() ? parseReaisInput(form.fixoClt) : 0;
    } catch {
      toast.error("Valor fixo inválido");
      return;
    }

    const corpo = sistema
      ? { nome: form.nome.trim(), colunaFlash: form.colunaFlash }
      : { nome: form.nome.trim(), colunaFlash: form.colunaFlash, pj: form.pj, clt: form.clt, fixoPj, fixoClt };

    setSalvando(true);
    try {
      const lista =
        editando === "novo"
          ? await api.post<BeneficioCatalogo[]>("/beneficios/tipos", corpo)
          : await api.patch<BeneficioCatalogo[]>(`/beneficios/tipos/${(editando as BeneficioCatalogo).tipo}`, corpo);
      setCatalogo(lista);
      setEditando(null);
      toast.success(editando === "novo" ? "Benefício criado" : "Benefício atualizado");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao salvar");
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async () => {
    if (!excluindo) return;
    try {
      await api.delete(`/beneficios/tipos/${excluindo.tipo}`);
      toast.success("Benefício excluído");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao excluir");
    } finally {
      setExcluindo(null);
    }
  };

  const ativos = catalogo.filter((b) => b.ativo);
  const fixo = (b: BeneficioCatalogo, vale: boolean, valor: number) =>
    !vale ? <span className="text-muted-foreground">—</span> : valor > 0 ? formatCents(valor) : <span className="text-muted-foreground">não é fixo</span>;

  return (
    <section className="overflow-hidden rounded-xl border bg-card">
      <div className="flex flex-wrap items-start justify-between gap-4 px-6 pb-4 pt-5">
        <div className="flex flex-[1_1_420px] flex-col gap-1.5">
          <h3 className="text-[15px] font-semibold">Benefícios</h3>
          <p className="max-w-[680px] text-[13px] leading-relaxed text-muted-foreground">
            Dê o nome que a empresa usa e escolha em qual coluna da planilha da Flash o benefício sai. Benefícios
            na mesma coluna são somados. O <strong className="text-foreground">fixo</strong> é o que todo
            colaborador ativo do vínculo recebe em cada nova folha de benefícios; o valor próprio no cadastro do
            colaborador tem prioridade. Benefícios não entram na folha de pagamento.
          </p>
        </div>
        <Button size="sm" onClick={() => abrir("novo")} disabled={carregando}>
          <Plus />
          Novo benefício
        </Button>
      </div>
      {carregando ? (
        <Loader2 className="mx-6 mb-6 h-5 w-5 animate-spin text-muted-foreground" />
      ) : ativos.length === 0 ? (
        <p className="px-6 pb-6 text-sm text-muted-foreground">Nenhum benefício cadastrado.</p>
      ) : (
        <Table className="min-w-[760px] border-t">
          <TableHeader>
            <TableRow>
              <TableHead className="pl-6">Benefício</TableHead>
              <TableHead>Coluna na Flash</TableHead>
              <TableHead>Vale para</TableHead>
              <TableHead className="text-right">Fixo PJ</TableHead>
              <TableHead className="text-right">Fixo CLT</TableHead>
              <TableHead className="w-[84px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {ativos.map((b) => (
              <TableRow key={b.tipo}>
                <TableCell className="py-[11px] pl-6 font-medium">
                  <span className="flex items-center gap-2">
                    {b.nome}
                    {b.sistema && (
                      <span className="rounded border px-1.5 py-px text-[10px] font-semibold text-muted-foreground">
                        automático
                      </span>
                    )}
                  </span>
                </TableCell>
                <TableCell className="py-[11px] text-muted-foreground">{b.colunaNome}</TableCell>
                <TableCell className="py-[11px]">{[b.pj && "PJ", b.clt && "CLT"].filter(Boolean).join(" e ")}</TableCell>
                <TableCell className="py-[11px] text-right">
                  {b.sistema ? <span className="text-muted-foreground">do cadastro</span> : fixo(b, b.pj, b.fixoPj)}
                </TableCell>
                <TableCell className="py-[11px] text-right">{fixo(b, b.clt, b.fixoClt)}</TableCell>
                <TableCell className="py-1.5 pr-3 text-right">
                  <span className="inline-flex gap-0.5">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-[30px] w-[30px] text-muted-foreground"
                      onClick={() => abrir(b)}
                      aria-label={`Editar ${b.nome}`}
                      title="Editar"
                    >
                      <Pencil className="!size-3.5" />
                    </Button>
                    {!b.sistema && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-[30px] w-[30px] text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => setExcluindo(b)}
                        aria-label={`Excluir ${b.nome}`}
                        title="Excluir"
                      >
                        <Trash2 className="!size-3.5" />
                      </Button>
                    )}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={editando !== null} onOpenChange={(v) => !v && setEditando(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editando === "novo" ? "Novo benefício" : "Editar benefício"}</DialogTitle>
            <DialogDescription>
              {sistema
                ? "O complemento de salário de cada PJ vem do cadastro dele. Aqui muda só o nome e a coluna da Flash."
                : "Mudanças valem para as próximas folhas de benefícios."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ben-nome">Nome</Label>
              <Input
                id="ben-nome"
                value={form.nome}
                onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                maxLength={80}
                placeholder="Ex.: Vale Mercado"
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label>Coluna na planilha da Flash</Label>
              <Select value={form.colunaFlash} onValueChange={(v) => setForm((f) => ({ ...f, colunaFlash: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a coluna" />
                </SelectTrigger>
                <SelectContent>
                  {colunas.map((c) => (
                    <SelectItem key={c.tipo} value={c.tipo}>
                      {c.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!sistema && (
              <div className="flex flex-col gap-2.5 rounded-[10px] border px-3.5 py-3">
                <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                  Vale para
                </span>
                {(
                  [
                    { chave: "pj", fixo: "fixoPj", rotulo: "PJ" },
                    { chave: "clt", fixo: "fixoClt", rotulo: "CLT" },
                  ] as const
                ).map((v) => (
                  <div key={v.chave} className="flex items-center gap-3">
                    <label className="flex w-16 cursor-pointer items-center gap-2 text-sm">
                      <Checkbox
                        checked={form[v.chave]}
                        onCheckedChange={(c) => setForm((f) => ({ ...f, [v.chave]: c === true }))}
                      />
                      {v.rotulo}
                    </label>
                    <Input
                      value={form[v.fixo]}
                      onChange={(e) => setForm((f) => ({ ...f, [v.fixo]: maskReaisInput(e.target.value) }))}
                      disabled={!form[v.chave]}
                      inputMode="numeric"
                      placeholder="Fixo (R$) — vazio = não é fixo"
                      className="h-9"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditando(null)} disabled={salvando}>
              Cancelar
            </Button>
            <Button onClick={() => void salvar()} disabled={salvando}>
              {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!excluindo} onOpenChange={(v) => !v && setExcluindo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir {excluindo?.nome}?</AlertDialogTitle>
            <AlertDialogDescription>
              Some das próximas folhas e os valores próprios dos colaboradores para ele são apagados. Folhas de
              benefícios já lançadas continuam mostrando o valor.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => void excluir()}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
